"use client";

import {
  extractUserFromAuthResponse,
  getCurrentUserSession,
} from "@/services/auth";
import { resolveActiveWorkspaceContext } from "@/lib/active-workspace";
import { fetchUserWorkspaces } from "@/services/workspace-service";
import { useWorkspaceStore } from "@/store/workspace/workspace-store";
import { AUTH_CONFIG } from "@/config/auth";

// User type for client-side authentication
export interface User {
  id: string;
  email?: string;
  name?: string;
  avatar?: string;
  picture?: string;
  display_name?: string;
  roles?: string[];
  permissions?: string[];
  screens?: string[];
  features?: string[];
  user_profile?: {
    display_name?: string;
    email?: string;
    first_name?: string;
    last_name?: string;
    id?: string;
    user_id?: string;
    picture_url?: string;
  };
  account?: {
    user_id: string;
    username: string;
  };
}

// Authentication result interface
export interface AuthResult {
  isAuthenticated: boolean;
  user: User | null;
  error?: string;
}

export interface ValidateClientAuthOptions {
  workspaceId?: string;
  organizationId?: string;
  appSessionId?: string;
  skipWorkspaceBootstrap?: boolean;
}

function isSuccessStatus(status?: string): boolean {
  if (!status) return false;
  const normalizedStatus = status.toLowerCase();
  return normalizedStatus === "ok" || normalizedStatus === "success";
}

/**
 * Client-side authentication validation
 * Checks if user is authenticated by calling localhost:81
 */
export async function validateClientAuth(
  options: ValidateClientAuthOptions = {},
): Promise<AuthResult> {
  try {
    // Skip auth if disabled
    if (!AUTH_CONFIG.enableAuth) {
      return {
        isAuthenticated: true,
        user: null,
      };
    }

    const activeWorkspaceContext = resolveActiveWorkspaceContext();
    const workspaceId = options.workspaceId ?? activeWorkspaceContext.workspaceId;
    const organizationId =
      options.organizationId ?? activeWorkspaceContext.organizationId;

    // Get user session data (from /auth/me with fallback to legacy session endpoint)
    const sessionResponse = await getCurrentUserSession({
      workspaceId,
      organizationId,
      appSessionId: options.appSessionId,
    });
    const userData = extractUserFromAuthResponse(sessionResponse);

    if (isSuccessStatus(sessionResponse.status) && userData) {
      // Fetch user workspaces after successful session validation
      if (!options.skipWorkspaceBootstrap) {
        try {
          console.log("[Client Auth] Fetching user workspaces...");
          const workspaces = await fetchUserWorkspaces();
          console.log("[Client Auth] User workspaces:", workspaces);

          // Store workspaces in Zustand store (will auto-select first if none selected)
          useWorkspaceStore.getState().setWorkspaces(workspaces);
        } catch (workspaceError) {
          console.error(
            "[Client Auth] Failed to fetch workspaces:",
            workspaceError
          );
          // Don't fail authentication if workspace fetch fails
        }
      }

      return {
        isAuthenticated: true,
        user: userData as User,
      };
    }

    return {
      isAuthenticated: false,
      user: null,
      error: "Invalid session response",
    };
  } catch (error) {
    console.error("[Client Auth] Validation error:", error);
    return {
      isAuthenticated: false,
      user: null,
      error:
        error instanceof Error ? error.message : "Authentication check failed",
    };
  }
}

/**
 * Check if current path should skip authentication
 */
export function shouldSkipAuth(
  pathname: string,
  skipPaths: readonly string[] = AUTH_CONFIG.skipPaths,
): boolean {
  return skipPaths.some((path) => {
    const normalizedPath = path === "/" ? path : path.replace(/\/$/, "");
    if (normalizedPath === "/") {
      return pathname === "/";
    }
    return (
      pathname === normalizedPath ||
      pathname.startsWith(`${normalizedPath}/`) ||
      pathname.endsWith(normalizedPath) ||
      pathname.includes(`${normalizedPath}/`)
    );
  });
}

// Note: getClientAuthHeaders() is available in src/services/api-url-service.ts as getAuthHeaders()
// Note: clearClientAuth() was unused and removed
